/**
 * Executor: takes a validated payload and performs preview/commit.
 * We keep this minimal to avoid touching existing functionality.
 * - Preview helpers are NO-OP for now (safe). We can wire real ghost later.
 * - Commit uses your RectangleLibrary + ProjectManager + CanvasEngine.
 */

import ProjectManager from "../../canvas/ProjectManager";
import InstanceLibrary from "../../libraries/RectangleLibrary";
import CanvasEngine from "../../canvas/CanvasEngine";
import layerLibrary from "../../canvas/LayerLibrary";
import layoutEngine from "../../libraries/layoutEngine";
//import InstanceLibrary from "../../libraries/RectangleLibrary";
/** Show a temporary preview (ghost) — placeholder (safe NO-OP for now). */




export function commitDelete(payload){
  const { cell, layerId, region, size, ids, selected } = payload;

  let toDelete = [];

  // 1) selected
  if (selected && window.canvasEngine?.selectedRects?.length) {
    toDelete.push(...window.canvasEngine.selectedRects.map(r => ({ id:r.id, layerId:r.layerId })));
  }

  // 2) explicit ids
  if (ids?.length) {
    const all = InstanceLibrary.getAllInstances?.() || {};
    const layers = layerId ? [layerId] : Object.keys(all[cell]||{});
    for (const lid of layers) {
      const arr = InstanceLibrary.getInstancesByCellAndLayer(cell, lid);
      for (const r of arr) if (ids.includes(r.id)) toDelete.push({ id:r.id, layerId: lid });
    }
  }

  // 3) filtered by region / size / layer
  const scanLayers = layerId ? [layerId] : Object.keys((InstanceLibrary.getAllInstances?.()[cell])||{});
  for (const lid of scanLayers) {
    const arr = InstanceLibrary.getInstancesByCellAndLayer(cell, lid);
    for (const r of arr) {
      if (r.type !== "rectangle") continue;
      if (size && !(approx(r.width,size.w) && approx(r.height,size.h))) continue;
      if (region && !inside(r, region)) continue;
     // If NO filters at all:
     // - If layerId is provided → delete all rectangles on that layer
     // - If layerId missing → skip (safety)
     if (!selected && !ids && !region && !size && !layerId) continue;
      toDelete.push({ id:r.id, layerId: lid });
    }
  }

  // dedupe
  const seen = new Set();
  toDelete = toDelete.filter(x => {
    const k = `${x.layerId}:${x.id}`; if (seen.has(k)) return false; seen.add(k); return true;
  });

  // Apply delete
  for (const item of toDelete) {
    InstanceLibrary.deleteRectangle?.(cell, item.layerId, item.id);
    layoutEngine.deleteElement?.(cell, item.id);
  }

  // refresh canvas
  try { window.canvasEngine?.draw?.(); } catch {}

  return { count: toDelete.length, items: toDelete };
}

function approx(a,b,eps=1e-9){ if(b==null) return true; return Math.abs(Number(a)-Number(b))<eps; }
function inside(r, box){
  const x1 = Math.min(r.x, r.x + r.width),  x2 = Math.max(r.x, r.x + r.width);
  const y1 = Math.min(r.y, r.y + r.height), y2 = Math.max(r.y, r.y + r.height);
  return x1>=box.x1 && y1>=box.y1 && x2<=box.x2 && y2<=box.y2;
}
export function showGhost(/* ready */) {
  // If later you add CanvasEngine.addPreviewRect(), call it here and return its id.
  return { previewId: null };
}

/** Clear preview (if any) — placeholder (safe NO-OP). */
export function clearGhost(previewId) {
  // If you implement preview ids later, remove them here.
  void previewId;
}

/**
 * Commit the rectangle(s) to the model.
 * ready = { cell, layerId, x, y, w, h, rotation, repeat:{nx,ny,dx,dy} }
 * Returns last created element id (for logging/UI).
 */
export function commitRectangle(ready) {
  const {
    cell,
    layerId,
    x,
    y,
    w,
    h,
    rotation = 0,
    repeat = { nx: 1, ny: 1, dx: 0, dy: 0 },
  } = ready;

  // Ensure the correct cell is active/selected (idempotent)
  try {
    if (typeof ProjectManager?.setSelectedCell === "function") {
      ProjectManager.setSelectedCell(cell);
    } else if (window?.layoutEngine?.showAsTop) {
      window.layoutEngine.showAsTop(cell);
    }
  } catch {
    // ignore if selection API differs; commit still goes to the named cell
  }

  let lastId = null;

  // Create nx × ny array if requested
  const nx = Math.max(1, Math.floor(repeat?.nx ?? 1));
  const ny = Math.max(1, Math.floor(repeat?.ny ?? 1));
  const dx = Number(repeat?.dx ?? 0);
  const dy = Number(repeat?.dy ?? 0);

  for (let iy = 0; iy < ny; iy++) {
  for (let ix = 0; ix < nx; ix++) {
    const rx = x + ix * dx;
    const ry = y + iy * dy;

    // Resolve layer numbers + style
    let L = 1, D = 0;
    if (typeof layerId === "string" && /^\d+\s*:\s*\d+$/.test(layerId)) {
      const parts = layerId.split(":");
      L = Number(parts[0]); D = Number(parts[1]);
    }
    const layerObj =
      layerLibrary.getLayerById?.(`${L}:${D}`) ||
      layerLibrary.getAllLayers?.().find(l =>
        (l.layer_number === L || l.layer === L) &&
        (l.datatype_number === D || l.datatype === D)
      ) || {};

    const opt = {
      rotation,
      layer: String(L),
      datatype: String(D),
      layerNumber: L,
      datatypeNumber: D,
      // pick styles if available
      color: layerObj.color || layerObj.stroke || undefined,
      fill: layerObj.fill || layerObj.color || undefined,
      stroke: layerObj.stroke || layerObj.color || undefined,
      opacity: layerObj.opacity ?? 1,
      layerName: layerObj.layer_name || layerObj.name || undefined,
    };

    // ✅ RectangleLibrary expects (cellName, layerId, x, y, width, height, options)
    lastId = InstanceLibrary.addRectangle(cell, `${L}:${D}`, rx, ry, w, h, opt);
  }
}

  // Redraw canvas after mutations
  try {
    if (typeof CanvasEngine?.redraw === "function") {
      CanvasEngine.redraw();
    } else if (window?.canvasEngine?.draw) {
      window.canvasEngine.draw();
    }
  } catch {
    /* ignore */
  }

  return lastId;
}

export default { showGhost, clearGhost, commitRectangle };
